/**
 * crawlEngine.js — Real schema detection via live site crawl
 * Fetches the domain, parses HTML for JSON-LD blocks, and returns
 * actual pass/fail status for each schema type.
 */

// ── Proxy fetch (Electron IPC first, then CORS proxy fallback) ───────────────
async function fetchSite(domain) {
  const clean = domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const targetUrl = 'https://' + clean;

  // 1. Electron main-process fetch — no CORS, always works
  if (window.sharkOS?.invoke) {
    try {
      const r = await window.sharkOS.invoke('fetch-url', { url: targetUrl, timeout: 12000 });
      if (r?.success && r.body && r.body.length > 200) return r.body;
    } catch {}
  }

  // 2. allorigins CORS proxy
  try {
    const r = await fetch(
      `https://api.allorigins.win/get?url=${encodeURIComponent(targetUrl)}`,
      { signal: AbortSignal.timeout(10000) }
    );
    if (r.ok) {
      const d = await r.json();
      if (d.contents && d.contents.length > 200) return d.contents;
    }
  } catch {}

  // 3. corsproxy.io
  try {
    const r = await fetch(
      `https://corsproxy.io/?${encodeURIComponent(targetUrl)}`,
      { signal: AbortSignal.timeout(10000) }
    );
    if (r.ok) {
      const text = await r.text();
      if (text && text.length > 200) return text;
    }
  } catch {}

  return null;
}

// ── Extract all JSON-LD blocks from HTML ──────────────────────────────────────
function extractJsonLd(html) {
  const blocks = [];
  const regex = /<script[^>]+type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim());
      // Handle both single objects and arrays
      if (Array.isArray(parsed)) blocks.push(...parsed);
      else blocks.push(parsed);
    } catch {}
  }
  return blocks;
}

// ── Get @type from a block (handles @graph) ───────────────────────────────────
function getTypes(block) {
  const types = [];
  if (block['@type']) {
    const t = block['@type'];
    if (Array.isArray(t)) types.push(...t);
    else types.push(t);
  }
  if (block['@graph']) {
    block['@graph'].forEach(item => types.push(...getTypes(item)));
  }
  return types;
}

// ── Check if HTML has meta/content freshness signals ─────────────────────────
function checkContentFreshness(html) {
  const now = new Date();
  const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(now - 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // Check for last-modified meta, article published dates, sitemap lastmod
  const datePatterns = [
    /dateModified["'\s:]+["']?(\d{4}-\d{2}-\d{2})/i,
    /datePublished["'\s:]+["']?(\d{4}-\d{2}-\d{2})/i,
    /article:modified_time.*content=["'](\d{4}-\d{2}-\d{2})/i,
    /lastmod.*?(\d{4}-\d{2}-\d{2})/i,
  ];

  for (const pattern of datePatterns) {
    const m = html.match(pattern);
    if (m) {
      const date = m[1];
      if (date >= thirtyDaysAgo) return { status: 'Pass', detail: `Content updated recently (${date})` };
      if (date >= ninetyDaysAgo) return { status: 'Warning', detail: `Last updated ${date} — aim for monthly updates` };
      return { status: 'Fail', detail: `Content not updated since ${date} — over 90 days old` };
    }
  }
  return { status: 'Warning', detail: 'Could not detect last update date' };
}

// ── Check for Q&A / FAQ content in HTML ──────────────────────────────────────
function checkQAContent(html) {
  const faqPatterns = [
    /frequently asked questions/i,
    /faq/i,
    /<details[^>]*>/i,
    /accordion/i,
    /question.*answer/i,
  ];
  const matches = faqPatterns.filter(p => p.test(html)).length;
  if (matches >= 2) return { status: 'Pass', detail: 'FAQ / Q&A content detected on page' };
  if (matches === 1) return { status: 'Warning', detail: 'Possible FAQ content — add structured Q&A markup' };
  return { status: 'Fail', detail: 'No FAQ or Q&A content detected' };
}

// ── Main export ───────────────────────────────────────────────────────────────
export async function crawlSite(domain) {
  if (!domain) {
    return {
      success: false, domain, jsonLdBlockCount: 0,
      localBusiness: { status: 'Fail', detail: 'No domain configured' },
      faqSchema:      { status: 'Fail', detail: 'No domain configured' },
      serviceSchema:  { status: 'Fail', detail: 'No domain configured' },
      reviewSchema:   { status: 'Fail', detail: 'No domain configured' },
      contentFreshness: { status: 'Fail', detail: 'No domain configured' },
      sitemap:        { status: 'Fail', detail: 'No domain configured' },
      qaContent:      { status: 'Fail', detail: 'No domain configured' },
      geoCoordinates: { status: 'Fail', detail: 'No domain configured' },
    };
  }

  const clean = domain.replace(/^https?:\/\//, '').replace(/\/$/, '');

  // Fetch homepage
  const html = await fetchSite(clean);

  if (!html) {
    return {
      success: false, domain: clean, jsonLdBlockCount: 0,
      localBusiness:    { status: 'Fail', detail: `Could not reach ${clean} — check domain or run via Electron` },
      faqSchema:        { status: 'Fail', detail: 'Site unreachable' },
      serviceSchema:    { status: 'Fail', detail: 'Site unreachable' },
      reviewSchema:     { status: 'Fail', detail: 'Site unreachable' },
      contentFreshness: { status: 'Warning', detail: 'Site unreachable — could not check' },
      sitemap:          { status: 'Fail', detail: 'Site unreachable' },
      qaContent:        { status: 'Fail', detail: 'Site unreachable' },
      geoCoordinates:   { status: 'Fail', detail: 'Site unreachable' },
    };
  }

  // Extract all JSON-LD blocks
  const blocks = extractJsonLd(html);
  const allTypes = blocks.flatMap(b => getTypes(b));
  const typeSet = new Set(allTypes.map(t => t.toLowerCase()));

  // ── LocalBusiness ────────────────────────────────────────────────────────
  const localBizTypes = [
    'localbusiness','organization','restaurant','store','foodestablishment',
    'medicalorganization','dentist','physician','lawyer','accountant',
    'realestateoragent','autodealer','hvacbusiness','plumber','electrician',
    'generalcontractor','homeandconstructionbusiness','professionalservice',
    'financialservice','insuranceagency','lodgingbusiness','hotel',
    'healthclub','beautysalon','hairsalon','spa',
  ];
  const hasLocalBiz = localBizTypes.some(t => typeSet.has(t));
  const localBizBlock = blocks.find(b => {
    const types = getTypes(b).map(t => t.toLowerCase());
    return localBizTypes.some(t => types.includes(t));
  });

  let localBusiness;
  if (hasLocalBiz) {
    const missingFields = [];
    if (localBizBlock) {
      if (!localBizBlock.name) missingFields.push('name');
      if (!localBizBlock.address && !localBizBlock.streetAddress) missingFields.push('address');
      if (!localBizBlock.telephone) missingFields.push('telephone');
      if (!localBizBlock.openingHours && !localBizBlock.openingHoursSpecification) missingFields.push('hours');
    }
    if (missingFields.length === 0) {
      localBusiness = { status: 'Pass', detail: `${getTypes(localBizBlock)[0]} schema found — all key fields present` };
    } else {
      localBusiness = { status: 'Warning', detail: `LocalBusiness schema found but missing: ${missingFields.join(', ')}` };
    }
  } else {
    // Check for inline microdata as fallback
    const hasMicrodata = /itemtype=["']https?:\/\/schema\.org\/(LocalBusiness|Organization)/i.test(html);
    if (hasMicrodata) {
      localBusiness = { status: 'Warning', detail: 'Schema.org microdata found — JSON-LD preferred for AI crawlers' };
    } else {
      localBusiness = { status: 'Fail', detail: 'No LocalBusiness or Organization schema detected' };
    }
  }

  // ── FAQ Schema ───────────────────────────────────────────────────────────
  const hasFaq = typeSet.has('faqpage') || typeSet.has('faq');
  const faqBlock = blocks.find(b => getTypes(b).some(t => t.toLowerCase() === 'faqpage'));
  let faqSchema;
  if (hasFaq && faqBlock) {
    const qCount = (faqBlock.mainEntity || []).length;
    faqSchema = { status: 'Pass', detail: `FAQPage schema found — ${qCount} Q&A${qCount !== 1 ? 's' : ''} structured` };
  } else if (hasFaq) {
    faqSchema = { status: 'Pass', detail: 'FAQ schema detected' };
  } else {
    faqSchema = { status: 'Fail', detail: 'No FAQPage schema found — add Q&A markup for AI citation' };
  }

  // ── Service Schema ───────────────────────────────────────────────────────
  const hasService = typeSet.has('service') || typeSet.has('offercatalog') || typeSet.has('offer');
  let serviceSchema;
  if (hasService) {
    const sCount = blocks.filter(b => getTypes(b).some(t => ['service','offer'].includes(t.toLowerCase()))).length;
    serviceSchema = { status: 'Pass', detail: `Service/Offer schema found — ${sCount} service block${sCount !== 1 ? 's' : ''}` };
  } else {
    serviceSchema = { status: 'Fail', detail: 'No Service schema — AI cannot enumerate your offerings' };
  }

  // ── Review Schema ────────────────────────────────────────────────────────
  const hasReview = typeSet.has('aggregaterating') || typeSet.has('review');
  let reviewSchema;
  if (hasReview) {
    const ratingBlock = blocks.find(b => getTypes(b).some(t => t.toLowerCase() === 'aggregaterating') ||
      b.aggregateRating);
    const rating = ratingBlock?.aggregateRating?.ratingValue || ratingBlock?.ratingValue;
    const count  = ratingBlock?.aggregateRating?.reviewCount || ratingBlock?.reviewCount;
    reviewSchema = {
      status: 'Pass',
      detail: rating ? `AggregateRating found — ${rating}★ (${count || '?'} reviews)` : 'Review schema found',
    };
  } else {
    reviewSchema = { status: 'Fail', detail: 'No AggregateRating schema — star ratings won\'t show in AI snippets' };
  }

  // ── Geo Coordinates ──────────────────────────────────────────────────────
  const hasGeo = blocks.some(b => b.geo || b.latitude || b.longitude ||
    (b['@graph'] && b['@graph'].some(g => g.geo)));
  const geoCoordinates = hasGeo
    ? { status: 'Pass', detail: 'GeoCoordinates found — enables AI "near me" responses' }
    : { status: 'Fail', detail: 'No GeoCoordinates — add lat/lng for local AI search' };

  // ── Sitemap ──────────────────────────────────────────────────────────────
  let sitemap = { status: 'Fail', detail: 'Sitemap not detected on homepage' };
  // Check for sitemap link in <head>
  if (/<link[^>]+rel=["']sitemap["'][^>]*>/i.test(html)) {
    sitemap = { status: 'Pass', detail: 'Sitemap link found in page head' };
  } else if (/sitemap\.xml/i.test(html)) {
    sitemap = { status: 'Warning', detail: 'Sitemap referenced — submit to Google & Bing Search Console' };
  }

  // ── Content Freshness ────────────────────────────────────────────────────
  const contentFreshness = checkContentFreshness(html);

  // ── Q&A Content ──────────────────────────────────────────────────────────
  const qaContent = checkQAContent(html);

  return {
    success: true,
    domain: clean,
    jsonLdBlockCount: blocks.length,
    localBusiness,
    faqSchema,
    serviceSchema,
    reviewSchema,
    contentFreshness,
    sitemap,
    qaContent,
    geoCoordinates,
    // Raw data for debugging
    _allTypes: allTypes,
    _blockCount: blocks.length,
  };
}
